import { useState, useMemo, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';
import { EMAIL_TEMPLATES, templatePreview, CAMPAIGN_CSS, AI_PROMPT_TRENDS, MERGE_TAGS } from './campaignAssets';
import { buildCampaignPayload } from './buildCampaignPayload';
import { ConfirmModal } from './ConfirmModal';

const SENDER_ADDRESS = 'Campaign@gotaps.eu';
const STEPS = ['Recipients', 'Setup', 'Content', 'Launch'];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
};

const EMPTY_FORM = {
  // Step 0
  recipientMode: 'all_profiles', // 'all_profiles' | 'all_leads' | 'specific'
  selectedEmails: [],
  pickerSearch: '',
  // Step 1
  name: '',
  subject: '',
  preheader: '',
  // Step 2
  templateId: '',
  body: '',
  cta_text: '',
  cta_url: '',
  // Step 3
  schedule: 'now', // 'now' | 'scheduled' | 'draft'
  schedDate: '',
  schedTime: '',
};

// Reverse-engineer wizard state from a saved draft/scheduled campaign so the
// user can resume editing exactly where they left off.
function hydrateForm(existing, profiles, leads) {
  if (!existing) return EMPTY_FORM;

  const recipients = Array.isArray(existing.recipients) ? existing.recipients : [];
  const profileEmails = profiles.map((p) => p.email).filter(Boolean);
  const leadEmails = leads.map((l) => l.email).filter(Boolean);
  const setEq = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

  let recipientMode = 'specific';
  if (profileEmails.length && setEq(recipients, profileEmails)) recipientMode = 'all_profiles';
  else if (leadEmails.length && setEq(recipients, leadEmails)) recipientMode = 'all_leads';

  let schedule = 'now';
  let schedDate = '';
  let schedTime = '';
  if (existing.status === 'draft') schedule = 'draft';
  else if (existing.status === 'scheduled' && existing.scheduled_at) {
    schedule = 'scheduled';
    const dt = new Date(existing.scheduled_at);
    if (!Number.isNaN(dt.getTime())) {
      schedDate = dt.toISOString().slice(0, 10);
      schedTime = dt.toTimeString().slice(0, 5);
    }
  }

  return {
    ...EMPTY_FORM,
    recipientMode,
    selectedEmails: recipients,
    name: existing.name || '',
    subject: existing.subject || '',
    preheader: existing.preheader || '',
    body: existing.message || '',
    cta_text: existing.cta_text || '',
    cta_url: existing.cta_url || '',
    schedule,
    schedDate,
    schedTime,
  };
}

export const ModalNewCampaign = memo(function ModalNewCampaign({
  profiles = [],
  leads = [],
  existing = null,
  onClose,
  onSaved,
}) {
  const { user, showToast } = useApp();
  const senderName = user?.company_name || user?.name || 'Gotaps';
  const replyTo = user?.email || '';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => hydrateForm(existing, profiles, leads));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  }, []);

  // Esc / X / backdrop → confirm discard if there are unsaved changes
  const requestClose = useCallback(() => {
    if (dirty) { setConfirmDiscard(true); return; }
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const profileEmails = useMemo(() => profiles.map((p) => p.email).filter(Boolean), [profiles]);
  const leadEmails = useMemo(() => leads.map((l) => l.email).filter(Boolean), [leads]);

  const recipientList = useMemo(() => {
    if (form.recipientMode === 'all_profiles') return profileEmails;
    if (form.recipientMode === 'all_leads') return leadEmails;
    return form.selectedEmails;
  }, [form.recipientMode, form.selectedEmails, profileEmails, leadEmails]);

  const recipientCount = recipientList.length;

  const emailToName = useMemo(() => {
    const m = new Map();
    profiles.forEach((p) => { if (p.email) m.set(p.email, p.name || p.username || p.email); });
    leads.forEach((l) => { if (l.email) m.set(l.email, l.name || l.email); });
    return m;
  }, [profiles, leads]);

  const togglePick = (email) => {
    setForm((f) => {
      const has = f.selectedEmails.includes(email);
      return { ...f, selectedEmails: has ? f.selectedEmails.filter((e) => e !== email) : [...f.selectedEmails, email] };
    });
    setDirty(true);
  };

  const filteredProfiles = useMemo(() => {
    const q = form.pickerSearch.toLowerCase().trim();
    if (!q) return profiles;
    return profiles.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.username || '').toLowerCase().includes(q));
  }, [profiles, form.pickerSearch]);

  const filteredLeads = useMemo(() => {
    const q = form.pickerSearch.toLowerCase().trim();
    if (!q) return leads;
    return leads.filter((l) =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q));
  }, [leads, form.pickerSearch]);

  const pickTemplate = (tpl) => {
    setForm((f) => ({
      ...f,
      templateId: tpl.id,
      subject: tpl.subject,
      body: tpl.body,
      cta_text: tpl.cta_text,
    }));
    setDirty(true);
    setErrors((prev) => ({ ...prev, body: '', subject: '' }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (recipientCount === 0) e.recipients = 'Select at least one recipient';
    }
    if (s === 1) {
      if (!form.name.trim()) e.name = 'Campaign name is required';
      if (!form.subject.trim()) e.subject = 'Subject is required';
      else if (form.subject.length > 255) e.subject = 'Maximum 255 characters';
    }
    if (s === 2) {
      if (!form.templateId) e.template = 'Pick a template to continue — choose Blank to start from scratch';
      const plain = form.body.replace(/<[^>]*>/g, '').trim();
      if (form.schedule !== 'draft') {
        if (!plain) e.body = 'Message is required';
        else if (plain.length < 20) e.body = 'Minimum 20 characters';
      }
      if (form.cta_url && !/^https?:\/\//i.test(form.cta_url)) e.cta_url = 'Must start with http:// or https://';
    }
    if (s === 3 && form.schedule === 'scheduled') {
      if (!form.schedDate || !form.schedTime) e.schedule = 'Pick a date and time';
      else {
        const when = new Date(`${form.schedDate}T${form.schedTime}`);
        if (when <= new Date()) e.schedule = 'Schedule must be in the future';
      }
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    // Final validation: every step that the chosen schedule cares about.
    const all = { ...validateStep(0), ...validateStep(1), ...validateStep(2), ...validateStep(3) };
    if (Object.keys(all).length) {
      setErrors(all);
      // Jump to the first step with errors so the user sees them.
      if (all.recipients) setStep(0);
      else if (all.name || all.subject) setStep(1);
      else if (all.template || all.body || all.cta_url) setStep(2);
      else setStep(3);
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildCampaignPayload(form, {
        recipients: recipientList,
        senderName,
      });

      const saved = existing?.id
        ? await api.updateCampaign(existing.id, payload)
        : await api.createCampaign(payload);

      const verb = form.schedule === 'draft' ? 'Draft saved'
        : form.schedule === 'scheduled' ? 'Campaign scheduled'
        : `Sent to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`;
      showToast(`✓ ${verb}.`);
      setDirty(false);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      const msg = err.errors
        ? Object.values(err.errors).flat()[0]
        : err.message || 'Save failed';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const stepCls = (i) => i < step ? 'done' : i === step ? 'act' : 'pend';
  const selTpl = EMAIL_TEMPLATES.find((t) => t.id === form.templateId);

  return createPortal(
    <>
      <style>{CAMPAIGN_CSS}</style>
      {confirmDiscard && (
        <ConfirmModal
          title="Discard changes?"
          message="Your draft will not be saved. Use “Save as Draft” on the Launch step if you want to come back to it later."
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          danger
          onConfirm={onClose}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
      <div className="ec-overlay" onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
        <div className="ec-drawer" onClick={(e) => e.stopPropagation()}>
          {/* Header + step bar */}
          <div className="ec-hd">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(8,145,178,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19 }}>📧</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 720, color: '#0f172a', letterSpacing: '-.3px' }}>
                    {existing?.id ? 'Edit Campaign' : 'New Email Campaign'}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 1 }}>
                    {step === 0 && 'Choose who receives this campaign'}
                    {step === 1 && 'Campaign details and subject line'}
                    {step === 2 && 'Pick a template or write your own'}
                    {step === 3 && 'Review and choose when to send'}
                  </div>
                </div>
              </div>
              <button onClick={requestClose} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.07)', cursor: 'pointer', fontSize: 15, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div className="ec-steps">
              {STEPS.map((lbl, i) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div className={`ec-step-item ${stepCls(i)}`} style={{ flex: 1 }}>
                    <div className="ec-step-num">{i < step ? '✓' : i + 1}</div>
                    <span className="ec-step-lbl">{lbl}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="ec-step-con" />}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="ec-body">
            {step === 0 && (
              <StepRecipients
                form={form} set={set} errors={errors}
                profileEmails={profileEmails} leadEmails={leadEmails}
                profiles={filteredProfiles} leads={filteredLeads}
                emailToName={emailToName}
                togglePick={togglePick}
                recipientCount={recipientCount}
              />
            )}
            {step === 1 && (
              <StepSetup
                form={form} set={set} errors={errors}
                senderName={senderName} replyTo={replyTo}
              />
            )}
            {step === 2 && (
              <StepContent
                form={form} set={set} errors={errors}
                pickTemplate={pickTemplate} selTpl={selTpl}
                senderName={senderName}
                showToast={showToast}
              />
            )}
            {step === 3 && (
              <StepLaunch
                form={form} set={set} errors={errors}
                recipientCount={recipientCount}
                selTpl={selTpl}
              />
            )}
          </div>

          {/* Footer */}
          <div className="ec-ft">
            {step > 0
              ? <button className="btn bs" onClick={back} disabled={submitting}>← Back</button>
              : <button className="btn bs" onClick={requestClose} disabled={submitting}>Cancel</button>}
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{step + 1} of {STEPS.length}</div>
            {step < STEPS.length - 1
              ? <button className="btn bp" onClick={next} disabled={submitting} style={{ minWidth: 120, justifyContent: 'center' }}>Continue →</button>
              : <button className="btn bp" onClick={submit} disabled={submitting} style={{ minWidth: 160, justifyContent: 'center' }}>
                  {submitting ? '⏳ Saving…' : (
                    form.schedule === 'now' ? '🚀 Launch Campaign'
                    : form.schedule === 'scheduled' ? '📅 Schedule Send'
                    : '💾 Save Draft'
                  )}
                </button>}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
});

// ─── Step 0 ──────────────────────────────────────────────────────────
const StepRecipients = ({ form, set, errors, profileEmails, leadEmails, profiles, leads, emailToName, togglePick, recipientCount }) => (
  <div className="ec-step">
    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Who receives this campaign?</div>
    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 22 }}>Send to all team profiles, all captured leads, or hand-pick specific contacts.</div>

    {[
      { val: 'all_profiles', ico: '👥', col: 'rgba(8,145,178,.1)', label: 'All Profiles', sub: 'Every team member profile.', count: `${profileEmails.length} profiles` },
      { val: 'all_leads', ico: '🎯', col: 'rgba(5,150,105,.1)', label: 'All Leads', sub: 'Every lead captured by your team.', count: `${leadEmails.length} leads` },
      { val: 'specific', ico: '✦', col: 'rgba(124,58,237,.1)', label: 'Pick Specific', sub: 'Hand-pick individual recipients across profiles and leads.', count: form.recipientMode === 'specific' && form.selectedEmails.length ? `${form.selectedEmails.length} selected` : null },
    ].map((opt) => (
      <div key={opt.val}
        className={`ec-seg-opt${form.recipientMode === opt.val ? ' sel' : ''}`}
        onClick={() => set('recipientMode', opt.val)}
      >
        <div className="ec-seg-ico" style={{ background: opt.col }}>{opt.ico}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 680, color: '#0f172a', marginBottom: 3 }}>{opt.label}</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{opt.sub}</div>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <div className={`ec-radio${form.recipientMode === opt.val ? ' on' : ''}`} />
          {opt.count && <div style={{ fontSize: 11, color: '#0891b2', fontWeight: 660 }}>{opt.count}</div>}
        </div>
      </div>
    ))}

    {form.recipientMode === 'specific' && (
      <div style={{ marginTop: 6 }}>
        {errors.recipients && (
          <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8, padding: '8px 12px', background: 'rgba(220,38,38,.06)', borderRadius: 8 }}>⚠ {errors.recipients}</div>
        )}
        <input className="inp" placeholder="🔍  Search by name or email…"
          value={form.pickerSearch} onChange={(e) => set('pickerSearch', e.target.value)}
          style={{ marginBottom: 12, fontSize: 12.5 }} />

        <SectionHead label={`Profiles (${profiles.length})`} />
        {profiles.length === 0 && <Empty msg="No profiles match." />}
        {profiles.map((p) => (
          <PickRow key={`p-${p.id}`}
            label={p.name || p.username || p.email}
            sublabel={p.email}
            checked={form.selectedEmails.includes(p.email)}
            onClick={() => p.email && togglePick(p.email)}
          />
        ))}
        <div style={{ height: 14 }} />
        <SectionHead label={`Leads (${leads.length})`} />
        {leads.length === 0 && <Empty msg="No leads match." />}
        {leads.map((l) => (
          <PickRow key={`l-${l.id}`}
            label={l.name || l.email}
            sublabel={l.email}
            checked={form.selectedEmails.includes(l.email)}
            onClick={() => l.email && togglePick(l.email)}
          />
        ))}
      </div>
    )}

    {recipientCount > 0 && (
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(5,150,105,.06)', border: '1px solid rgba(5,150,105,.18)', borderRadius: 11, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>✅</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 660, color: '#059669' }}>
            {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} selected
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
            {form.recipientMode === 'all_profiles' && 'All team profiles'}
            {form.recipientMode === 'all_leads' && 'All captured leads'}
            {form.recipientMode === 'specific' && 'Hand-picked contacts'}
          </div>
        </div>
      </div>
    )}
  </div>
);

const SectionHead = ({ label }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
);

const Empty = ({ msg }) => (
  <div style={{ fontSize: 11.5, color: '#94a3b8', padding: '6px 10px 10px' }}>{msg}</div>
);

const PickRow = ({ label, sublabel, checked, onClick }) => (
  <div className={`ec-pick-row${checked ? ' sel' : ''}`} onClick={onClick}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12.5, fontWeight: 620, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sublabel}</div>
    </div>
    <div className={`ec-pick-cb${checked ? ' on' : ''}`}>{checked && '✓'}</div>
  </div>
);

// ─── Step 1 ──────────────────────────────────────────────────────────
const StepSetup = ({ form, set, errors, senderName, replyTo }) => (
  <div className="ec-step">
    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Campaign Details</div>
    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 22 }}>Give your campaign a name and define what shows up in the inbox.</div>

    <div style={{
      padding: '10px 14px',
      background: 'rgba(8,145,178,.06)',
      border: '1px solid rgba(8,145,178,.18)',
      borderRadius: 10,
      fontSize: 11.5, color: 'var(--t2)', lineHeight: 1.5, marginBottom: 18,
    }}>
      <span style={{ fontSize: 13, marginRight: 6 }}>💡</span>
      Sent as <b style={{ color: '#0f172a' }}>{senderName}</b>
      <span style={{ color: '#64748b' }}> &lt;{SENDER_ADDRESS}&gt;</span>
      {replyTo && <> — replies route to <b style={{ color: '#0f172a' }}>{replyTo}</b>.</>}
    </div>

    <div className="fg">
      <label>Campaign Name {errors.name && <span style={{ color: 'var(--red)', fontWeight: 400 }}>— {errors.name}</span>}</label>
      <input className="inp" placeholder="e.g. Q2 Spring Outreach"
        value={form.name} maxLength={160}
        onChange={(e) => set('name', e.target.value)}
        style={errors.name ? { borderColor: 'var(--red)' } : {}} />
    </div>

    <div className="fg">
      <label>Subject Line {errors.subject && <span style={{ color: 'var(--red)', fontWeight: 400 }}>— {errors.subject}</span>}</label>
      <input className="inp" placeholder="e.g. Your Q2 opportunity — don't miss it"
        value={form.subject} maxLength={255}
        onChange={(e) => set('subject', e.target.value)}
        style={errors.subject ? { borderColor: 'var(--red)' } : {}} />
      <div style={{ fontSize: 11, color: 'var(--t4)', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
        <span>{form.subject.length < 30 ? '💡 Add more detail' : form.subject.length < 60 ? '✅ Good length' : '⚠ Consider shortening'}</span>
        <span>{form.subject.length}/255</span>
      </div>
    </div>

    <div className="fg">
      <label>Preheader <span style={{ fontWeight: 400, color: 'var(--t4)' }}>— preview text shown in the inbox (optional)</span></label>
      <input className="inp" placeholder="Short teaser shown after the subject line…"
        value={form.preheader} maxLength={255}
        onChange={(e) => set('preheader', e.target.value)} />
    </div>
  </div>
);

// ─── Step 2 ──────────────────────────────────────────────────────────
const AI_TEMPLATE_ID = 'ai-generated';

const StepContent = ({ form, set, errors, pickTemplate, selTpl, senderName, showToast }) => {
  // Default to "ai" tab when the body was AI-generated (e.g. user is editing
  // a draft they previously generated); otherwise "template" tab.
  const [tab, setTab] = useState(form.templateId === AI_TEMPLATE_ID ? 'ai' : 'template');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // When set, holds the prompt to use after the user confirms the replace.
  const [pendingPrompt, setPendingPrompt] = useState(null);

  const generate = async (promptText) => {
    setAiLoading(true);
    try {
      const { body } = await api.generateAiEmail({
        prompt: promptText,
        subject: form.subject,
        recipientMode: form.recipientMode,
      });
      set('body', body);
      // Adopt the AI synthetic id so validation (which requires templateId)
      // passes without forcing the user to also pick a template.
      set('templateId', AI_TEMPLATE_ID);
      showToast?.('✓ AI generated your email body.');
    } catch (err) {
      showToast?.(err.message || 'AI generation failed', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const onGenerateClick = () => {
    const text = aiPrompt.trim();
    if (!text) {
      showToast?.('Type or pick a goal first', 'error');
      return;
    }
    // Only confirm if the body has real content. Empty/whitespace-only HTML is fine to overwrite.
    const hasContent = form.body.replace(/<[^>]*>/g, '').trim().length > 0;
    if (hasContent) {
      setPendingPrompt(text);
    } else {
      generate(text);
    }
  };

  const insertMergeTag = (tag) => {
    // Append to body — simpler than threading a Quill ref through props,
    // and the user can drag/cut the tag to where they want it.
    const html = form.body || '';
    const sep = html && !/(<\/p>|<br ?\/?>)\s*$/i.test(html) ? ' ' : '';
    set('body', html + sep + tag);
  };

  return (
    <div className="ec-step">
      {pendingPrompt && (
        <ConfirmModal
          title="Replace current email body?"
          message="Generating with AI will overwrite the email body you have so far. Your subject line stays."
          confirmLabel="Replace & generate"
          cancelLabel="Keep current"
          workingLabel="Generating content…"
          onConfirm={async () => { await generate(pendingPrompt); }}
          onClose={() => setPendingPrompt(null)}
        />
      )}

      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Email Content</div>
      <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20 }}>Choose a template or let AI write your email in seconds.</div>

      <div className="ec-content-tabs">
        <button type="button" className={`ec-content-tab${tab === 'template' ? ' act' : ''}`} onClick={() => setTab('template')}>
          📄 Choose Template
        </button>
        <button type="button" className={`ec-content-tab${tab === 'ai' ? ' act' : ''}`} onClick={() => setTab('ai')}>
          ✦ Generate with AI
        </button>
      </div>

      {tab === 'template' && (
        <>
          <div className="ec-tpl-grid">
            {EMAIL_TEMPLATES.map((tpl) => (
              <div key={tpl.id} className={`ec-tpl${form.templateId === tpl.id ? ' sel' : ''}`} onClick={() => pickTemplate(tpl)}>
                <div className="ec-tpl-preview">{templatePreview(tpl)}</div>
                {form.templateId === tpl.id && <div className="ec-tpl-check">✓</div>}
                <div className="ec-tpl-foot">
                  <div className="ec-tpl-name">{tpl.name}</div>
                  <div className="ec-tpl-tag" style={{ color: tpl.accent }}>{tpl.tag}</div>
                </div>
              </div>
            ))}
          </div>

          {!form.templateId && (
            <div style={{
              padding: '12px 14px', marginBottom: 18,
              background: errors.template ? 'rgba(220,38,38,.06)' : 'rgba(8,145,178,.06)',
              border: `1px solid ${errors.template ? 'rgba(220,38,38,.22)' : 'rgba(8,145,178,.18)'}`,
              borderRadius: 11, fontSize: 12.5,
              color: errors.template ? '#b91c1c' : '#475569',
            }}>
              {errors.template ? `⚠ ${errors.template}` : '👆 Pick a template above to continue. Choose Blank if you prefer to write everything from scratch.'}
            </div>
          )}

          {selTpl && (
            <div style={{ padding: '12px 14px', background: `${selTpl.accent}07`, border: `1px solid ${selTpl.accent}22`, borderRadius: 11, marginBottom: 18, fontSize: 12, color: '#475569' }}>
              <b style={{ color: selTpl.accent }}>✓ {selTpl.name}</b> — {selTpl.desc}
            </div>
          )}
        </>
      )}

      {tab === 'ai' && (
        <div className="ec-ai-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16 }}>✦</span>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>AI Email Generator</div>
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Describe your campaign goal and AI will write a professional email instantly.
          </div>

          <div className="ec-ai-chips">
            {AI_PROMPT_TRENDS.map((p) => (
              <button type="button" key={p}
                className={`ec-ai-chip${aiPrompt === p ? ' sel' : ''}`}
                onClick={() => setAiPrompt(p)}
                disabled={aiLoading}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="ec-ai-row">
            <input className="inp" placeholder="Describe your email goal…"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              maxLength={500}
              disabled={aiLoading}
            />
            <button type="button" className="btn bp"
              onClick={onGenerateClick}
              disabled={aiLoading || !aiPrompt.trim()}
              style={{ minWidth: 160, justifyContent: 'center', gap: 8 }}
            >
              {aiLoading
                ? <><span className="ec-spin" aria-hidden /> Generating content…</>
                : <>✦ Generate</>}
            </button>
          </div>

          {aiLoading && (
            <div style={{
              marginTop: 14, padding: '12px 14px',
              background: 'rgba(8,145,178,.06)',
              border: '1px solid rgba(8,145,178,.2)',
              borderRadius: 11,
              display: 'flex', alignItems: 'center', gap: 11,
              fontSize: 12.5, color: '#0e7490',
            }}>
              <span className="ec-spin" aria-hidden />
              <div>
                <div style={{ fontWeight: 660 }}>AI is writing your email…</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  Drafting a personalized message with your merge tags. This usually takes a couple of seconds.
                </div>
              </div>
            </div>
          )}
          {!aiLoading && form.templateId === AI_TEMPLATE_ID && form.body && (
            <div style={{ marginTop: 12, fontSize: 11.5, color: '#0891b2', fontWeight: 600 }}>
              ✓ AI body ready below — review and edit before continuing.
            </div>
          )}
          {errors.template && tab === 'ai' && !form.templateId && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#b91c1c' }}>⚠ Generate an email to continue.</div>
          )}
        </div>
      )}

      <div className="fg">
        <div className="ec-merge-bar">
          <label style={{ marginBottom: 0 }}>
            Email Body {errors.body && <span style={{ color: 'var(--red)', fontWeight: 400 }}>— {errors.body}</span>}
          </label>
          <div className="ec-merge-chips">
            {MERGE_TAGS.map((m) => (
              <button type="button" key={m.tag}
                className="ec-merge-chip"
                title={`Insert ${m.label} — replaced per recipient at send time`}
                onClick={() => insertMergeTag(m.tag)}
              >
                {m.tag}
              </button>
            ))}
          </div>
        </div>
        <div style={{
          border: `1px solid ${errors.body ? 'var(--red)' : 'rgba(0,0,0,.1)'}`,
          borderRadius: 11, overflow: 'hidden', background: 'rgba(255,255,255,.72)',
        }}>
          <ReactQuill theme="snow" value={form.body}
            onChange={(v) => set('body', v)}
            modules={QUILL_MODULES}
            placeholder="Write your email…" />
        </div>
      </div>

    <div className="frow">
      <div className="fg" style={{ marginBottom: 0 }}>
        <label>CTA Button Text <span style={{ fontWeight: 400, color: 'var(--t4)' }}>(optional)</span></label>
        <input className="inp" placeholder="e.g. Book a Call →"
          value={form.cta_text} maxLength={80}
          onChange={(e) => set('cta_text', e.target.value)} />
      </div>
      <div className="fg" style={{ marginBottom: 0 }}>
        <label>CTA URL {errors.cta_url && <span style={{ color: 'var(--red)', fontWeight: 400 }}>— {errors.cta_url}</span>}</label>
        <input className="inp" type="url" placeholder="https://…"
          value={form.cta_url}
          onChange={(e) => set('cta_url', e.target.value)}
          style={errors.cta_url ? { borderColor: 'var(--red)' } : {}} />
      </div>
    </div>

    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>Preview</div>
      <div className="ec-email-preview">
        <div className="ec-email-chrome">
          <div className="ec-email-dot" style={{ background: '#ff5f57' }} />
          <div className="ec-email-dot" style={{ background: '#ffbd2e' }} />
          <div className="ec-email-dot" style={{ background: '#28ca42' }} />
          <div style={{ flex: 1, height: 14, background: 'rgba(0,0,0,.06)', borderRadius: 4, marginLeft: 8 }} />
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>From: {senderName} &lt;{SENDER_ADDRESS}&gt;</div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{form.subject || '(subject not set)'}</div>
          {form.preheader && <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 10, fontStyle: 'italic' }}>{form.preheader}</div>}
          <div style={{ height: 1, background: '#f1f5f9', marginBottom: 14 }} />
          <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.7, maxHeight: 220, overflow: 'auto' }}
            dangerouslySetInnerHTML={{ __html: form.body || '<span style="color:#94a3b8;font-style:italic">Type your message…</span>' }} />
          {form.cta_text && (
            <div style={{ marginTop: 16 }}>
              <a href={form.cta_url || '#'} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', background: '#0891b2', color: '#fff', padding: '9px 20px', borderRadius: 8, fontSize: 12.5, fontWeight: 640, textDecoration: 'none' }}>
                {form.cta_text}
              </a>
            </div>
          )}
          <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9', fontSize: 10.5, color: '#94a3b8' }}>
            {senderName} · sent via Gotaps
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

// ─── Step 3 ──────────────────────────────────────────────────────────
const StepLaunch = ({ form, set, errors, recipientCount, selTpl }) => {
  const opts = [
    { val: 'now', ico: '⚡', lbl: 'Send Now', sub: 'Deliver immediately' },
    { val: 'scheduled', ico: '📅', lbl: 'Schedule', sub: 'Pick date and time' },
    { val: 'draft', ico: '📝', lbl: 'Save as Draft', sub: 'Come back later' },
  ];
  const minDate = new Date().toISOString().slice(0, 10);

  return (
    <div className="ec-step">
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Review &amp; Launch</div>
      <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 22 }}>Everything looks good? Choose when to send.</div>

      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '.09em', textTransform: 'uppercase', marginBottom: 12 }}>Sending Options</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {opts.map((opt) => (
          <div key={opt.val}
            className={`ec-sched-opt${form.schedule === opt.val ? ' sel' : ''}`}
            onClick={() => set('schedule', opt.val)}>
            <div style={{ fontSize: 24 }}>{opt.ico}</div>
            <div style={{ fontSize: 12.5, fontWeight: 680, color: '#0f172a', textAlign: 'center' }}>{opt.lbl}</div>
            <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>{opt.sub}</div>
          </div>
        ))}
      </div>

      {form.schedule === 'scheduled' && (
        <>
          <div className="frow" style={{ marginBottom: errors.schedule ? 8 : 18 }}>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label>Date</label>
              <input className="inp" type="date" min={minDate}
                value={form.schedDate} onChange={(e) => set('schedDate', e.target.value)} />
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label>Time</label>
              <input className="inp" type="time"
                value={form.schedTime} onChange={(e) => set('schedTime', e.target.value)} />
            </div>
          </div>
          {errors.schedule && (
            <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 18, padding: '8px 12px', background: 'rgba(220,38,38,.06)', borderRadius: 8 }}>⚠ {errors.schedule}</div>
          )}
        </>
      )}

      <div style={{ background: 'rgba(255,255,255,.88)', border: '1px solid rgba(0,0,0,.08)', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14 }}>Campaign Summary</div>
        {[
          { k: 'Campaign Name', ico: '📌', v: form.name || '—' },
          { k: 'Recipients', ico: '👥', v: `${recipientCount} ${form.recipientMode === 'all_profiles' ? '(all profiles)' : form.recipientMode === 'all_leads' ? '(all leads)' : '(picked)'}` },
          { k: 'Subject', ico: '✉️', v: form.subject || '—' },
          { k: 'Template', ico: '📄', v: selTpl?.name || 'Custom' },
          { k: 'CTA', ico: '🔗', v: form.cta_text ? `${form.cta_text} → ${form.cta_url || '(no link)'}` : '—' },
          { k: 'Send Time', ico: '⏰', v: form.schedule === 'now' ? 'Immediately' : form.schedule === 'scheduled' ? `${form.schedDate || '—'} ${form.schedTime || ''}` : 'Saved as draft' },
        ].map(({ k, ico, v }) => (
          <div key={k} className="ec-summary-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{ico}</span>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 560 }}>{k}</span>
            </div>
            <span style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600, maxWidth: 320, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
          </div>
        ))}
      </div>

      {form.schedule === 'now' && (
        <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg,rgba(5,150,105,.08),rgba(8,145,178,.06))', border: '1px solid rgba(5,150,105,.2)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>🚀</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 680, color: '#059669' }}>Ready to launch</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              Sending to <b style={{ color: '#0f172a' }}>{recipientCount} recipient{recipientCount !== 1 ? 's' : ''}</b> as soon as you click Launch.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
